import React from 'react';
import { Tag } from '@carbon/react';
import { useCrudConfig } from './provider';
import { ReferenceLoad, ReferenceOptionsByLoad } from './useReferenceOptions';

/**
 * The declarative cell renderers (spec §6.3). Every value-reading kind goes through the
 * adapter's formatters, so a money convention or locale lives in exactly one place.
 */
export type ColumnDisplay<TEntity> =
  | { kind: 'text'; subtitle?: (row: TEntity) => string }
  | { kind: 'currency' }
  | { kind: 'number'; unit?: (row: TEntity) => string }
  | { kind: 'date'; relative?: boolean }
  | { kind: 'boolean' }
  | { kind: 'tag'; map: Record<string, { labelKey: string; type: string }> }
  | { kind: 'reference'; load: ReferenceLoad; optionLabel: (option: any) => string };

function numericOrNull(value: unknown): number | null {
  if (value == null) {
    return null;
  }
  // `Number('') === 0`: an empty-string cell must stay "no value", not coerce to 0 → "0K".
  if (value === '') {
    return null;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(numeric) ? null : numeric;
}

/**
 * Every kind below is a COMPONENT, not an inline render, because each needs the config
 * (formatters, translate) and renderDisplay is invoked from a `.map()` inside CrudTable —
 * hooks in a plain function called per row would be a Rules-of-Hooks violation whose hook
 * count changes with row count.
 */
function TextCell({
  row,
  columnKey,
  subtitle,
}: {
  row: Record<string, unknown>;
  columnKey: string;
  subtitle?: (row: any) => string;
}) {
  const value = row[columnKey];
  const secondLine = subtitle?.(row as never);
  return (
    <div>
      {value as React.ReactNode}
      {secondLine ? <small style={{ display: 'block', opacity: 0.6 }}>{secondLine}</small> : null}
    </div>
  );
}

function CurrencyCell({ row, columnKey }: { row: Record<string, unknown>; columnKey: string }) {
  const { useFormatCurrency } = useCrudConfig();
  // eslint-disable-next-line react/hooks -- injected hook, stable per provider mount.
  const formatCurrency = useFormatCurrency();
  return <>{formatCurrency(numericOrNull(row[columnKey]))}</>;
}

function NumberCell({
  row,
  columnKey,
  unit,
}: {
  row: Record<string, unknown>;
  columnKey: string;
  unit?: (row: any) => string;
}) {
  const value = row[columnKey];
  // An absent value renders nothing at all — not a dangling unit floating in the cell.
  if (value === '' || value == null) {
    return null;
  }
  return (
    <>
      {value as React.ReactNode}
      {unit ? <span> {unit(row as never)}</span> : null}
    </>
  );
}

function DateCell({
  row,
  columnKey,
  relative,
}: {
  row: Record<string, unknown>;
  columnKey: string;
  relative?: boolean;
}) {
  const { formatDate } = useCrudConfig();
  return <>{formatDate(row[columnKey] as string | Date | null | undefined, { relative })}</>;
}

// A badge, like the tag columns beside it, so an on/off state reads at a glance in a long
// list; "No" stays neutral gray rather than red, since off is a normal state, not an error.
function BooleanCell({ row, columnKey }: { row: Record<string, unknown>; columnKey: string }) {
  const { translate } = useCrudConfig();
  const on = Boolean(row[columnKey]);
  return <Tag type={on ? 'green' : 'gray'}>{translate(on ? 'common.yes' : 'common.no')}</Tag>;
}

function TagCell({
  row,
  columnKey,
  map,
}: {
  row: Record<string, unknown>;
  columnKey: string;
  map: Record<string, { labelKey: string; type: string }>;
}) {
  const { translate } = useCrudConfig();
  const value = row[columnKey];
  const entry = value == null ? undefined : map[String(value)];
  if (!entry) {
    // An unmapped value is data the config does not know about; surface it verbatim rather
    // than pretending it is one of the known tags.
    return <Tag type="gray">{value == null ? '' : String(value)}</Tag>;
  }
  return <Tag type={entry.type}>{translate(entry.labelKey)}</Tag>;
}

function ReferenceCell({
  row,
  columnKey,
  optionLabel,
  optionsById,
}: {
  row: Record<string, unknown>;
  columnKey: string;
  optionLabel: (option: any) => string;
  optionsById: Map<string, unknown> | undefined;
}) {
  const option = optionsById?.get(String(row[columnKey]));
  return <>{option ? optionLabel(option) : ''}</>;
}

/**
 * Renders one cell from its display descriptor. `columnKey` reads the row's value — the
 * display objects are column-agnostic by design, so one descriptor can be reused across
 * columns. `render` still wins over `display` when both are present; that precedence lives
 * in CrudTable.
 *
 * Reference kinds read the option map the TABLE resolved (see useReferenceOptionsByLoad): the
 * map is passed in rather than fetched here, so one reference column costs one observer no
 * matter how many rows are on the page.
 */
export function renderDisplay<TEntity>(
  display: ColumnDisplay<TEntity>,
  row: TEntity,
  columnKey: string,
  references: ReferenceOptionsByLoad
): React.ReactNode {
  const record = row as Record<string, unknown>;
  switch (display.kind) {
    case 'text':
      return <TextCell row={record} columnKey={columnKey} subtitle={display.subtitle} />;
    case 'currency':
      return <CurrencyCell row={record} columnKey={columnKey} />;
    case 'number':
      return <NumberCell row={record} columnKey={columnKey} unit={display.unit} />;
    case 'date':
      return <DateCell row={record} columnKey={columnKey} relative={display.relative} />;
    case 'boolean':
      return <BooleanCell row={record} columnKey={columnKey} />;
    case 'tag':
      return <TagCell row={record} columnKey={columnKey} map={display.map} />;
    case 'reference':
      return (
        <ReferenceCell
          row={record}
          columnKey={columnKey}
          optionLabel={display.optionLabel}
          optionsById={references.get(display.load)}
        />
      );
  }
}
